/**
 * Vendor Onboarding Status Hook
 * 
 * Tracks and aggregates the completion status of vendor onboarding tasks.
 * This hook helps vendors understand what steps remain to fully activate
 * their account and begin receiving RFQs and job opportunities.
 * 
 * **Onboarding Checklist:**
 * 1. **Basic Verification** - Profile verified by admin
 * 2. **Stripe Configuration** - Payment account setup complete
 * 3. **First Bid Submitted** - Active participation in the marketplace
 * 4. **Profile Completion** - All required fields filled out
 * 
 * **Profile Completion Criteria:**
 * - Company name and description
 * - Service categories
 * - Geographic coverage
 * - Capabilities and certifications
 * - Contact information
 * 
 * **Status Indicators:**
 * - `needsOnboarding` - Overall flag if any steps are incomplete
 * - `hasBasicVerification` - Admin has verified the vendor
 * - `stripeConfigured` - Stripe Connect account is active
 * - `hasSubmittedBid` - Vendor has bid on at least one RFQ
 * - `profileCompletionPercentage` - 0-100% profile completeness
 * - `missingItems` - Array of specific incomplete items
 * 
 * @returns {Object} Vendor onboarding status
 * @returns {boolean} needsOnboarding - Whether onboarding is incomplete
 * @returns {boolean} hasBasicVerification - Profile verified status
 * @returns {boolean} stripeConfigured - Payment setup status
 * @returns {boolean} hasSubmittedBid - Bid submission status
 * @returns {number} profileCompletionPercentage - 0-100 completion percentage
 * @returns {string[]} missingItems - List of incomplete onboarding tasks
 * @returns {boolean} isLoading - Loading state
 * 
 * @example
 * // Display onboarding progress
 * const {
 *   needsOnboarding,
 *   profileCompletionPercentage,
 *   missingItems
 * } = useVendorOnboardingStatus();
 * 
 * if (needsOnboarding) {
 *   return (
 *     <OnboardingBanner>
 *       Profile {profileCompletionPercentage}% complete
 *       <ul>
 *         {missingItems.map(item => <li>{item}</li>)}
 *       </ul>
 *     </OnboardingBanner>
 *   );
 * }
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

export interface VendorOnboardingStatus {
  needsOnboarding: boolean;
  hasBasicVerification: boolean;
  hasRequestedVerification: boolean;
  stripeConfigured: boolean;
  hasSubmittedBid: boolean;
  profileCompletionPercentage: number;
  missingItems: string[];
}

export const useVendorOnboardingStatus = () => {
  const { user, organization } = useAuth();

  return useQuery<VendorOnboardingStatus>({
    queryKey: ['vendor-onboarding-status', user?.id, organization?.id],
    queryFn: async () => {
      if (!user?.id || !organization?.id) {
        return {
          needsOnboarding: true,
          hasBasicVerification: false,
          hasRequestedVerification: false,
          stripeConfigured: false,
          hasSubmittedBid: false,
          profileCompletionPercentage: 0,
          missingItems: ['Complete vendor profile', 'Request verification', 'Configure payment options'],
        };
      }

      // Fetch vendor profile with verification status and address fields
      const profileQuery = await (supabase
        .from('vendor_profiles_enhanced')
        .select('id, is_verified, verification_level, business_description, business_name, street_address, city, state, zip_code')
        .eq('user_id', user.id)
        .maybeSingle() as unknown as Promise<any>);
      const profile = profileQuery.data;

      // Check if vendor has submitted any bids
      const bidsQuery = await supabase
        .from('bids')
        .select('id')
        .eq('vendor_user_id', user.id)
        .limit(1);
      const bids = bidsQuery.data;

      // Check Stripe Connect status
      const stripeQuery = await (supabase
        .from('stripe_accounts')
        .select('charges_enabled, payouts_enabled')
        .eq('organization_id', organization.id)
        .maybeSingle() as unknown as Promise<any>);
      const stripeAccount = stripeQuery.data;

      // Check for pending verification request (only if we have a profile)
      let pendingVerificationRequest = null;
      if (profile?.id) {
        const verificationRequestQuery = await supabase
          .from('verification_requests')
          .select('id, status')
          .eq('vendor_profile_id', profile.id)
          .eq('status', 'pending')
          .limit(1);
        pendingVerificationRequest = verificationRequestQuery.data;
      }

      const hasBasicVerification = profile?.is_verified === true && 
        profile?.verification_level !== 'none';
      
      const hasRequestedVerification = (pendingVerificationRequest?.length ?? 0) > 0;
      
      const stripeConfigured = stripeAccount?.charges_enabled === true && 
        stripeAccount?.payouts_enabled === true;
      
      const hasSubmittedBid = (bids?.length ?? 0) > 0;

      // Calculate profile completion
      let completionScore = 0;
      const sections: string[] = [];

      // Profile is considered complete when core required address fields exist
      // (business_description is optional, so we check required fields instead)
      const hasCompletedProfile = !!(
        profile?.street_address &&
        profile?.city &&
        profile?.state &&
        profile?.zip_code
      );

      if (hasCompletedProfile) {
        completionScore += 33;
      } else {
        sections.push('Complete vendor profile');
      }

      if (hasBasicVerification) {
        completionScore += 34;
      } else if (hasRequestedVerification) {
        // Partial credit for submitting verification request
        completionScore += 17;
        // Don't add to missing items - handled separately in banner
      } else {
        sections.push('Request verification');
      }

      if (stripeConfigured) {
        completionScore += 33;
      } else {
        sections.push('Configure payment options');
      }

      const needsOnboarding = !hasBasicVerification || !stripeConfigured;

      return {
        needsOnboarding,
        hasBasicVerification,
        hasRequestedVerification,
        stripeConfigured,
        hasSubmittedBid,
        profileCompletionPercentage: completionScore,
        missingItems: sections,
      };
    },
    enabled: !!user?.id && !!organization?.id,
  });
};
